import * as React from 'react'

import { Slider as SliderPrimitive } from 'radix-ui'

import { cn } from './cn'

export interface SliderProps {
  value?: number | number[]
  defaultValue?: number | number[]
  onChange?: (event: unknown, value: number | number[]) => void
  onChangeCommitted?: (event: unknown, value: number | number[]) => void
  min?: number
  max?: number
  step?: number
  orientation?: 'horizontal' | 'vertical'
  disabled?: boolean
  size?: 'small' | 'medium'
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
  valueLabelDisplay?: 'on' | 'auto' | 'off'
  valueLabelFormat?: (value: number) => React.ReactNode
  'aria-label'?: string
  'aria-valuemin'?: number
  'aria-valuemax'?: number
  'aria-valuenow'?: number
  title?: string
  id?: string
  name?: string
  role?: string
  className?: string
  style?: React.CSSProperties
}

const Slider = React.forwardRef<HTMLSpanElement, SliderProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      onChangeCommitted,
      min = 0,
      max = 100,
      step = 1,
      orientation = 'horizontal',
      disabled,
      size = 'medium',
      color = 'primary',
      valueLabelDisplay = 'off',
      valueLabelFormat,
      className,
      style,
      id,
      name,
      title,
      role,
      ...aria
    },
    ref,
  ) => {
    const toArray = (v: number | number[] | undefined) =>
      v === undefined ? undefined : Array.isArray(v) ? v : [v]
    const fromArray = (v: number[]) => (v.length === 1 ? v[0] : v)

    const isVertical = orientation === 'vertical'
    const currentValues = toArray(value) ?? toArray(defaultValue) ?? [min]

    return (
      <SliderPrimitive.Root
        ref={ref}
        id={id}
        name={name}
        title={title}
        value={toArray(value)}
        defaultValue={toArray(defaultValue)}
        onValueChange={(v) => onChange?.(undefined, fromArray(v))}
        onValueCommit={(v) => onChangeCommitted?.(undefined, fromArray(v))}
        min={min}
        max={max}
        step={step}
        orientation={orientation}
        disabled={disabled}
        aria-label={aria['aria-label']}
        className={cn(
          'relative flex touch-none select-none items-center',
          isVertical ? 'h-full w-5 flex-col justify-center' : 'h-5 w-full',
          disabled && 'pointer-events-none opacity-40',
          className,
        )}
        style={style}
      >
        <SliderPrimitive.Track
          className={cn(
            'relative grow rounded-full bg-primary/[0.18]',
            isVertical ? 'w-1.5' : 'h-1.5',
          )}
        >
          <SliderPrimitive.Range
            className={cn(
              'absolute rounded-full bg-primary',
              isVertical ? 'w-full' : 'h-full',
            )}
          />
        </SliderPrimitive.Track>
        {currentValues.map((v, i) => (
          <SliderPrimitive.Thumb
            key={i}
            className={cn(
              'group relative block rounded-full border-2 border-primary bg-white shadow-menu transition-shadow',
              size === 'small' ? 'h-3.5 w-3.5' : 'h-[18px] w-[18px]',
              'hover:shadow-[0_0_0_7px_rgb(var(--ps-primary-rgb)/0.14)]',
              'focus:outline-none focus-visible:shadow-[0_0_0_7px_rgb(var(--ps-primary-rgb)/0.14)]',
            )}
          >
            {valueLabelDisplay !== 'off' && (
              <span
                className={cn(
                  'absolute left-1/2 -translate-x-1/2 rounded-md bg-[#211826]/95 px-1.5 py-0.5 text-xs font-medium text-white shadow-menu',
                  '-top-7 whitespace-nowrap',
                  valueLabelDisplay === 'auto' &&
                    'hidden group-hover:block group-focus:block',
                )}
              >
                {valueLabelFormat ? valueLabelFormat(v) : v}
              </span>
            )}
          </SliderPrimitive.Thumb>
        ))}
      </SliderPrimitive.Root>
    )
  },
)
Slider.displayName = 'Slider'

export default Slider
